import { z } from "zod";
import { qvcLifecycleCohortIdSchema } from "./qvc-fulfillment.js";
import { ulidSchema } from "./schemas.js";
export const qvcResumeSchemaVersionSchema = z.literal("2026-09-16.1");
export const qvcResumePurposeSchema = z.literal("TELL_FIRST_MEMORY");
export const qvcResumeGrantStateSchema = z.enum(["PENDING", "CONSUMED", "REVOKED", "EXPIRED"]);
export const qvcResumeTokenSchema = z.string().regex(/^[A-Za-z0-9_-]{43}$/u);
export const qvcResumeGrantSchema = z
    .object({
    schemaVersion: qvcResumeSchemaVersionSchema,
    id: ulidSchema,
    tokenHash: z.string().regex(/^[a-f0-9]{64}$/u),
    subject: z.string().trim().min(1).max(128),
    userId: ulidSchema,
    bookId: ulidSchema,
    cohortId: qvcLifecycleCohortIdSchema,
    purpose: qvcResumePurposeSchema,
    state: qvcResumeGrantStateSchema,
    deliveryKey: z.string().regex(/^[a-f0-9]{64}$/u),
    startAttempts: z.number().int().min(0).max(5),
    test: z.boolean(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    expiresAt: z.string().datetime(),
    expiresAtEpoch: z.number().int().positive(),
    consumedAt: z.string().datetime().optional(),
    revokedAt: z.string().datetime().optional(),
    revocationReason: z
        .string()
        .regex(/^[A-Z][A-Z0-9_]{1,63}$/u)
        .optional(),
})
    .strict()
    .superRefine((grant, context) => {
    if (Math.floor(Date.parse(grant.expiresAt) / 1_000) !== grant.expiresAtEpoch)
        context.addIssue({
            code: "custom",
            path: ["expiresAtEpoch"],
            message: "The resume expiry fields must identify the same second.",
        });
    if (grant.state === "CONSUMED" && !grant.consumedAt)
        context.addIssue({
            code: "custom",
            path: ["consumedAt"],
            message: "A consumed resume grant requires its consumption time.",
        });
    if (grant.state === "REVOKED" && (!grant.revokedAt || !grant.revocationReason))
        context.addIssue({
            code: "custom",
            path: ["revokedAt"],
            message: "A revoked resume grant requires its time and reason.",
        });
    if (!["CONSUMED", "REVOKED"].includes(grant.state) && grant.consumedAt)
        context.addIssue({
            code: "custom",
            path: ["consumedAt"],
            message: "Only a consumed or subsequently revoked resume grant can retain a consumption time.",
        });
    if (grant.state !== "REVOKED" && (grant.revokedAt || grant.revocationReason))
        context.addIssue({
            code: "custom",
            path: ["revokedAt"],
            message: "Only a revoked resume grant can have revocation metadata.",
        });
});
/**
 * The reconnect secret (S3, 2026-11-02): issued once, on the first start of an unused link, and
 * held in the page's memory only. A link already used reopens its own session only with it, so
 * the link alone (forwarded, or left in an email) can't sign anyone in again. Its shape is the
 * link token's: 32 random bytes, base64url.
 */
export const qvcResumeReconnectSecretSchema = qvcResumeTokenSchema;
export const startQvcResumeCommandSchema = z
    .object({
    token: qvcResumeTokenSchema,
    /** Required to reopen a session the link already began; ignored on a first start. */
    reconnectSecret: qvcResumeReconnectSecretSchema.optional(),
})
    .strict();
export const startQvcResumeResponseSchema = z
    .object({
    data: z
        .object({
        username: z.string().trim().email().max(254),
        clientId: z.string().min(1).max(128),
        region: z.string().regex(/^[a-z]{2}(?:-gov)?-[a-z]+-\d$/u),
        purpose: qvcResumePurposeSchema,
        expiresAt: z.string().datetime(),
        /** Issued on a first start only (S3); the page keeps it in memory to reconnect. */
        reconnectSecret: qvcResumeReconnectSecretSchema.optional(),
    })
        .strict(),
})
    .strict();
export const qvcResumeSessionResponseSchema = z
    .object({
    data: z
        .object({
        bookId: ulidSchema,
        purpose: qvcResumePurposeSchema,
        expiresAt: z.string().datetime(),
        taskState: z.enum(["READY", "COMPLETED"]),
    })
        .strict(),
})
    .strict();
export const saveQvcResumeTextCommandSchema = z
    .object({
    text: z.string().trim().min(20).max(20_000),
})
    .strict();
export const saveQvcResumeTextResponseSchema = z
    .object({
    data: z
        .object({
        bookId: ulidSchema,
        purpose: qvcResumePurposeSchema,
        sourceId: ulidSchema,
        savedAt: z.string().datetime(),
        alreadySaved: z.boolean(),
    })
        .strict(),
})
    .strict();
export const qvcResumeVoiceContentTypeSchema = z.enum([
    "audio/mp4",
    "audio/mpeg",
    "audio/wav",
    "audio/webm",
    "audio/x-m4a",
]);
export const createQvcResumeVoiceUploadCommandSchema = z
    .object({
    contentType: qvcResumeVoiceContentTypeSchema,
    byteSize: z
        .number()
        .int()
        .positive()
        .max(5 * 1024 * 1024),
    durationSeconds: z.number().positive().max(180),
    checksumSha256: z.string().regex(/^[a-f0-9]{64}$/u),
    providerDisclosureAccepted: z.literal(true),
})
    .strict();
export const createQvcResumeVoiceUploadResponseSchema = z
    .object({
    data: z
        .object({
        assetId: ulidSchema,
        uploadId: z.string().min(8).max(1_000),
        uploadUrl: z.string().url(),
        expiresInSeconds: z.literal(15 * 60),
        maximumDurationSeconds: z.literal(180),
        maximumBytes: z.literal(5 * 1024 * 1024),
        status: z.literal("UPLOADING"),
    })
        .strict(),
})
    .strict();
export const completeQvcResumeVoiceUploadCommandSchema = z
    .object({
    uploadId: z.string().min(8).max(1_000),
    etag: z.string().min(3).max(200),
})
    .strict();
export const qvcResumeVoiceStatusSchema = z.enum([
    "UPLOADING",
    "CHECKING",
    "TRANSCRIBING",
    "COMPLETED",
    "NEEDS_ATTENTION",
    "REJECTED",
    "SUPERSEDED",
]);
export const qvcResumeVoiceStatusResponseSchema = z
    .object({
    data: z
        .object({
        assetId: ulidSchema,
        status: qvcResumeVoiceStatusSchema,
    })
        .strict(),
})
    .strict();
//# sourceMappingURL=qvc-resume.js.map